<script setup lang="ts">
import states from '@base/common/states'

import { useVuelidate } from '@vuelidate/core'
import { computed, nextTick, reactive, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'

import type { Policy } from '@/types/policy'

type State = {
  phone: string
  email: string
  firstName: string
  lastName: string
  state: {
    value: string
    label: string
  }
}

const defaultState: State = {
  phone: '',
  email: '',
  firstName: '',
  lastName: '',
  state: {
    value: '',
    label: ''
  }
}

const state: State = reactive({ ...defaultState })

const formattedPhone = computed(() => {
  const cleaned = ('' + state.phone).replace(/\D/g, '') // Remove non-digit characters
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/) // Match groups of digits

  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`
  }

  return state.phone // Return the original value if it doesn't match the format
})

const searched = ref(false)
const searching = ref(false)

const route = useRoute()
const router = useRouter()

const results = ref<Partial<Policy>[]>([])

const { REQUIRED, REQUIRED_PHONE } = useValidation()

const dynamicValidation = computed(() => {
  const isRequiredFieldFilled = !!state.phone || !!state.email || !!state.firstName || !!state.lastName

  return {
    phone: {
      required: !isRequiredFieldFilled || state.phone ? REQUIRED_PHONE.required : false,
      minLength: state.phone ? REQUIRED_PHONE.minLength : true,
      maxLength: state.phone ? REQUIRED_PHONE.maxLength : true
    },
    email: {
      required: !isRequiredFieldFilled || state.email ? REQUIRED.required : false
    },
    firstName: {
      required: !isRequiredFieldFilled || state.firstName ? REQUIRED.required : false
    },
    lastName: {
      // Last name is always required if the first name is filled
      required: state.firstName
        ? REQUIRED.required
        : !isRequiredFieldFilled || state.lastName
          ? REQUIRED.required
          : false
    }
  }
})

const v$ = useVuelidate(dynamicValidation, state)

const resetValidation = () => {
  v$.value.$reset()
}

const search = async () => {
  const validated = await v$.value.$validate()

  if (!validated) {
    return
  }

  try {
    resetValidation()

    searched.value = true
    searching.value = true

    const { data } = await useApi('SearchContacts', {
      method: 'POST',
      body: {
        phone: formattedPhone.value,
        email: state.email,
        firstName: state.firstName,
        lastName: state.lastName,
        state: state.state.value,
        pageNumber: 0,
        pageSize: 50
      }
    })

    const query = Object.fromEntries(
      Object.entries({
        phone: formattedPhone.value,
        email: state.email,
        firstName: state.firstName,
        lastName: state.lastName,
        state: state.state.value
      }).filter(([, value]) => !!value)
    )

    router.replace({ query })

    if (data?.results) {
      // Sort the results by ranking
      const sortedResults = data.results.sort((a: Policy, b: Policy) => b.ranking - a.ranking)

      results.value = sortedResults
    }
  } catch (error: any) {
    console.error(error)
  } finally {
    searching.value = false
  }
}

const resetSearch = () => {
  Object.assign(state, defaultState)
  results.value = []
  searched.value = false
  searching.value = false
  router.replace({ query: {} })
}

watch(
  () => route.query,
  async (query) => {
    // Destructure the query params
    const { phone = '', email = '', firstName = '', lastName = '', state: stateValue = '' } = query || {}

    // Update the state with the query params
    Object.assign(state, {
      phone: phone?.toString(),
      email: email?.toString(),
      firstName: firstName?.toString(),
      lastName: lastName?.toString(),
      state: { value: stateValue?.toString() }
    })

    // Wait for the next tick to ensure the state is updated before searching
    await nextTick()

    // Check if any of the search fields are in the route query string
    const hasQueryParams = ['phone', 'email', 'firstName', 'lastName', 'state'].some((param) => query[param])

    /*
     * If any of the search fields are in the route query string,
     * and we haven't already searched,
     * set the query params in the state and trigger a search.
     */
    if (hasQueryParams && !searched.value) {
      search()
    }

    /*
     * If all of the query params are empty,
     * and we have already searched,
     * reset the search.
     */
    if (Object.values(query).every((value) => !value) && searched.value) {
      resetSearch()
    }
  },
  { deep: true, immediate: true }
)
</script>

<template>
  <div class="space-y-5">
    <h3 class="text-lg font-semibold leading-tight -mb-1">Search by Contact Information</h3>
    <form
      :class="['xl:flex xl:space-x-2 xl:space-y-0 space-y-2', { 'opacity-75 pointer-events-none': searching }]"
      @submit.prevent="search"
    >
      <div class="xl:flex items-start xl:space-x-2 xl:space-y-0 space-y-2 flex-1">
        <UIFormGroup
          :validation="v$.phone"
          class="flex-1"
        >
          <UIFormLabel for="phone-number">Phone Number</UIFormLabel>
          <PhoneNumberInput
            id="phone-number"
            v-model="state.phone"
          />
        </UIFormGroup>
        <UIFormGroup
          :validation="v$.email"
          class="flex-1"
        >
          <UIFormLabel for="email">Email</UIFormLabel>
          <Input
            id="email"
            v-model="state.email"
          />
        </UIFormGroup>
        <UIFormGroup
          :validation="v$.firstName"
          class="flex-1"
        >
          <UIFormLabel for="first-name">First Name</UIFormLabel>
          <Input
            id="first-name"
            v-model="state.firstName"
          />
        </UIFormGroup>
        <UIFormGroup
          :validation="v$.lastName"
          class="flex-1"
        >
          <UIFormLabel for="last-name">Last Name</UIFormLabel>
          <Input
            id="last-name"
            v-model="state.lastName"
          />
        </UIFormGroup>
        <UIFormGroup
          :validation="v$.state"
          class="flex-1"
        >
          <UIFormLabel for="state">State</UIFormLabel>
          <UICombobox
            id="state"
            v-model="state.state"
            :items="states"
            variant="outlined"
            clearable
          />
        </UIFormGroup>
      </div>
      <div class="flex space-x-2 items-start xl:pt-[28px]">
        <UIButton
          color="primary"
          type="submit"
          :disabled="searching"
        >
          Search
        </UIButton>
      </div>
    </form>
    <div v-if="searched">
      <div v-if="searching">
        <Spinner />
      </div>
      <div
        v-else
        class="space-y-5"
      >
        <div :class="['font-bold', { 'text-primary': results.length > 0, 'text-danger': !results.length }]">
          {{ results.length }} results
        </div>
        <ResultsTable
          v-if="results.length > 0"
          :data="results"
          striped
          sticky-header
        />
      </div>
    </div>
  </div>
</template>
